import React, { useRef, useState } from "react";
import {
  Download,
  FileCode2,
  FileSpreadsheet,
  Loader2,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import {
  calcItemAmount,
  downloadInvoiceItemsTemplate,
  emptyInvoiceItem,
  parseInvoiceItemsFile,
  sumItemsSubtotal,
} from "./invoiceItemsImport";
import { parseInvoiceXmlFile } from "./invoiceXmlImport";

const formatMoney = (value) => {
  if (value === "" || value == null) return "";
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("vi-VN");
};

const parseMoney = (raw) => String(raw || "").replace(/\D/g, "");

const parseQty = (raw) => {
  let s = String(raw || "").replace(/[^\d.,]/g, "").replace(",", ".");
  const parts = s.split(".");
  if (parts.length > 2) s = `${parts[0]}.${parts.slice(1).join("")}`;
  return s;
};

/**
 * Bảng mặt hàng + import XML HĐĐT (ưu tiên) / Excel.
 * onChange(items)
 * onXmlImported?.({ header, items, warnings })
 */
export default function InvoiceItemsEditor({
  items = [],
  onChange,
  onXmlImported,
  invoiceType = "purchase",
}) {
  const xmlRef = useRef(null);
  const excelRef = useRef(null);
  const [importingXml, setImportingXml] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  const [tplLoading, setTplLoading] = useState(false);

  const subtotal = sumItemsSubtotal(items);

  const updateRow = (key, patch) => {
    onChange?.(
      items.map((row) => {
        if (row.key !== key) return row;
        const next = { ...row, ...patch };
        next.amount = String(
          calcItemAmount(next.quantity, next.unit_price) || ""
        );
        return next;
      })
    );
  };

  const addRow = () => onChange?.([...items, emptyInvoiceItem()]);

  const removeRow = (key) => {
    const next = items.filter((r) => r.key !== key);
    onChange?.(next.length ? next : [emptyInvoiceItem()]);
  };

  const handleDownloadTemplate = async () => {
    try {
      setTplLoading(true);
      await downloadInvoiceItemsTemplate();
    } catch (err) {
      console.error(err);
      showErrorToast("Không tải được file mẫu.");
    } finally {
      setTplLoading(false);
    }
  };

  const handleImportXml = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const name = file.name.toLowerCase();
    if (name && !/\.xml$/i.test(name)) {
      showErrorToast("Vui lòng chọn file XML hóa đơn điện tử (.xml).");
      return;
    }

    try {
      setImportingXml(true);
      const { header, items: parsed, warnings } = await parseInvoiceXmlFile(
        file,
        { invoiceType }
      );
      if (onXmlImported) {
        onXmlImported({ header, items: parsed, warnings });
      } else {
        onChange?.(parsed);
      }
      const warnHint = warnings?.length
        ? ` (${warnings.length} cảnh báo — kiểm tra lại form)`
        : "";
      showSuccessToast(
        `Đã nhập XML HĐĐT: ${parsed.length} mặt hàng${warnHint}.`
      );
    } catch (err) {
      console.error(err);
      showErrorToast(err?.message || "Không đọc được XML hóa đơn điện tử.");
    } finally {
      setImportingXml(false);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const name = file.name.toLowerCase();
    if (!/\.(xlsx|xls|csv)$/.test(name)) {
      showErrorToast("Vui lòng chọn file .xlsx, .xls hoặc .csv");
      return;
    }

    try {
      setImportingExcel(true);
      const { items: parsed, errors } = await parseInvoiceItemsFile(file);
      if (!parsed.length) {
        showErrorToast(
          errors[0] || "Không có dòng mặt hàng hợp lệ trong file."
        );
        return;
      }
      onChange?.(parsed);
      if (errors.length) {
        showSuccessToast(
          `Đã nhập ${parsed.length} dòng. ${errors.length} dòng lỗi bị bỏ qua.`
        );
      } else {
        showSuccessToast(`Đã nhập ${parsed.length} mặt hàng từ Excel.`);
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Không đọc được file Excel.");
    } finally {
      setImportingExcel(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => xmlRef.current?.click()}
          disabled={importingXml}
          className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 text-xs font-bold uppercase tracking-wide text-red-700 hover:bg-red-100 disabled:opacity-60"
          title="File XML từ phần mềm / cổng hóa đơn điện tử (TT 78)"
        >
          {importingXml ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <FileCode2 size={14} />
          )}
          Nhập XML HĐĐT
        </button>
        <input
          ref={xmlRef}
          type="file"
          accept=".xml,text/xml,application/xml"
          className="hidden"
          onChange={handleImportXml}
        />

        <button
          type="button"
          onClick={() => excelRef.current?.click()}
          disabled={importingExcel}
          className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold uppercase tracking-wide text-slate-600 hover:border-red-200 hover:text-red-700 disabled:opacity-60"
        >
          {importingExcel ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <FileSpreadsheet size={14} />
          )}
          Nhập Excel
        </button>
        <input
          ref={excelRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={handleImportExcel}
        />

        <button
          type="button"
          onClick={handleDownloadTemplate}
          disabled={tplLoading}
          className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-transparent px-2.5 text-xs font-semibold text-slate-400 hover:text-slate-600 disabled:opacity-60"
          title="Chỉ dùng khi không có XML — mẫu dòng hàng nội bộ"
        >
          {tplLoading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Download size={14} />
          )}
          Mẫu Excel
        </button>

        <button
          type="button"
          onClick={addRow}
          className="ml-auto inline-flex h-10 items-center gap-1.5 rounded-xl bg-gradient-to-r from-red-700 to-red-800 px-3 text-xs font-bold uppercase tracking-wide text-white shadow-sm"
        >
          <Plus size={14} />
          Thêm dòng
        </button>
      </div>

      <p className="text-[11px] text-slate-400 leading-relaxed">
        Ưu tiên file <span className="font-semibold text-slate-500">.xml</span>{" "}
        HĐĐT (NĐ 123 / TT 78) — tự điền số HĐ, ngày, đối tác, MST, VAT và mặt
        hàng. Excel chỉ khi không có XML.
      </p>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-100">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-50/80">
            <tr className="text-left text-[10px] font-black uppercase tracking-widest text-slate-400">
              <th className="px-3 py-2.5 w-10">#</th>
              <th className="px-3 py-2.5">Tên hàng *</th>
              <th className="px-3 py-2.5 w-24">ĐVT</th>
              <th className="px-3 py-2.5 w-28">SL *</th>
              <th className="px-3 py-2.5 w-36">Đơn giá *</th>
              <th className="px-3 py-2.5 w-36 text-right">Thành tiền</th>
              <th className="px-3 py-2.5 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items.map((row, idx) => (
              <tr key={row.key} className="align-top">
                <td className="px-3 py-2 text-xs font-bold text-slate-300">
                  {String(idx + 1).padStart(2, "0")}
                </td>
                <td className="px-3 py-2">
                  <input
                    value={row.item_name}
                    onChange={(e) =>
                      updateRow(row.key, { item_name: e.target.value })
                    }
                    placeholder="Tên mặt hàng / dịch vụ"
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm outline-none focus:border-red-400"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    value={row.unit}
                    onChange={(e) =>
                      updateRow(row.key, { unit: e.target.value })
                    }
                    placeholder="cái"
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm outline-none focus:border-red-400"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    inputMode="decimal"
                    value={row.quantity}
                    onChange={(e) =>
                      updateRow(row.key, { quantity: parseQty(e.target.value) })
                    }
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm tabular-nums outline-none focus:border-red-400"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    inputMode="numeric"
                    value={formatMoney(row.unit_price)}
                    onChange={(e) =>
                      updateRow(row.key, {
                        unit_price: parseMoney(e.target.value),
                      })
                    }
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm tabular-nums outline-none focus:border-red-400"
                    placeholder="0"
                  />
                </td>
                <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-800">
                  {formatMoney(
                    String(calcItemAmount(row.quantity, row.unit_price) || "")
                  ) || "—"}
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => removeRow(row.key)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title="Xóa dòng"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {items.map((row, idx) => (
          <div
            key={row.key}
            className="rounded-2xl border border-slate-100 bg-white p-3.5 shadow-sm"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Dòng {String(idx + 1).padStart(2, "0")}
              </span>
              <button
                type="button"
                onClick={() => removeRow(row.key)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 size={15} />
              </button>
            </div>
            <input
              value={row.item_name}
              onChange={(e) =>
                updateRow(row.key, { item_name: e.target.value })
              }
              placeholder="Tên mặt hàng *"
              className="mb-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium outline-none focus:border-red-400"
            />
            <div className="grid grid-cols-3 gap-2">
              <input
                value={row.unit}
                onChange={(e) => updateRow(row.key, { unit: e.target.value })}
                placeholder="ĐVT"
                className="rounded-xl border border-slate-200 px-2.5 py-2 text-sm outline-none focus:border-red-400"
              />
              <input
                inputMode="decimal"
                value={row.quantity}
                onChange={(e) =>
                  updateRow(row.key, { quantity: parseQty(e.target.value) })
                }
                placeholder="SL"
                className="rounded-xl border border-slate-200 px-2.5 py-2 text-sm tabular-nums outline-none focus:border-red-400"
              />
              <input
                inputMode="numeric"
                value={formatMoney(row.unit_price)}
                onChange={(e) =>
                  updateRow(row.key, { unit_price: parseMoney(e.target.value) })
                }
                placeholder="Đơn giá"
                className="rounded-xl border border-slate-200 px-2.5 py-2 text-sm tabular-nums outline-none focus:border-red-400"
              />
            </div>
            <p className="mt-2 text-right text-xs font-bold text-slate-700">
              Thành tiền:{" "}
              <span className="tabular-nums text-red-700">
                {formatMoney(
                  String(calcItemAmount(row.quantity, row.unit_price) || "")
                ) || "0"}{" "}
                ₫
              </span>
            </p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/80 px-3.5 py-2.5 text-sm">
        <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
          <Upload size={13} className="text-red-500" />
          Tổng trước VAT (từ mặt hàng)
        </span>
        <span className="font-black tabular-nums text-red-700">
          {subtotal.toLocaleString("vi-VN")} ₫
        </span>
      </div>
    </div>
  );
}
